import { motion } from 'framer-motion';
import { cn } from '../../lib/classNameHelpers';
import { Tooltip } from '../Tooltip/Tooltip';

type TabItem = {
  value: string;
  label: string;
  hidden?: boolean;
  disabled?: boolean;
  tooltip?: string;
};

type TabValues<T extends readonly TabItem[]> = T[number]['value'];

type ButtonTabsProps<T extends readonly TabItem[]> = {
  tabs: T;
  activeTabValue?: TabValues<T>;
  className?: string;
  onChange?: (tabId: TabValues<T>) => void;
  getBackgroundColor?: (activeTab: T[number]) => string;
};

export function ButtonTabs<T extends readonly TabItem[]>({
  tabs,
  onChange,
  className,
  activeTabValue,
  getBackgroundColor,
}: ButtonTabsProps<T>) {
  const filteredTabs = tabs.filter((tab) => !tab.hidden);
  const activeTabValueWithDefault = activeTabValue ?? filteredTabs[0]?.value;
  const activeTab = filteredTabs.find((tab) => tab.value === activeTabValueWithDefault);
  const handleTabClick = (tabId: TabValues<T>) => {
    const tab = filteredTabs.find((t) => t.value === tabId);
    if (tab && !tab.disabled) {
      onChange?.(tabId);
    }
  };

  if (!activeTab) return null;

  const backgroundColorClass = getBackgroundColor ? getBackgroundColor(activeTab) : 'bg-blue-500';

  return (
    <div className={cn('relative flex w-fit rounded-lg bg-backgroundHoverOverlay  p-1', className)}>
      {filteredTabs.map((tab) => {
        const isActive = tab.value === activeTabValueWithDefault;

        return (
          <div key={tab.value} className="relative flex-1">
            {isActive && (
              <motion.div
                layoutId="tab-indicator"
                className={backgroundColorClass}
                style={{
                  position: 'absolute',
                  zIndex: 10,
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  borderRadius: '6px',
                }}
                transition={{
                  type: 'spring',
                  stiffness: 400,
                  damping: 25,
                  mass: 0.8,
                }}
              />
            )}
            <button
              role="tab"
              disabled={tab.disabled}
              aria-selected={isActive}
              className={cn(
                'relative z-20 flex size-full flex-1 items-center justify-center text-nowrap p-1.5 text-sm font-medium outline-none transition-colors duration-200',
                tab.disabled
                  ? 'cursor-not-allowed text-contentTertiary'
                  : isActive
                    ? 'text-contentInversePrimary'
                    : 'text-contentSecondary hover:text-contentPrimary'
              )}
              onClick={() => handleTabClick(tab.value)}
            >
              {tab.tooltip ? (
                <Tooltip dottedUnderline={false} content={tab.tooltip}>
                  {tab.label}
                </Tooltip>
              ) : (
                tab.label
              )}
            </button>
          </div>
        );
      })}
    </div>
  );
}
